import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { TableSessionsFetchAPI } from "../../../api/TableSessions";
import { PrinterFetchAPI } from "../../../api/Printer";
import { printData } from "../../../utils/PrintData";
import { kinposQrPrintBridge } from "../../../utils/KinposQrPrintBridge";

// "Generar QR para pedir y pagar" (POS "Mesas", pre-pay flow). Mints a fresh single-use QR the
// moment it opens - generating it here is itself what authorizes the table (see
// services/tablePrepayTokens.js#generatePrepayQr on the backend), there is no separate approval
// step. Whoever scans it burns it immediately, so "Regenerar" is the only way to get a scannable
// QR again once one has been used (or just to swap it before anyone scans it).
//
// On a PAX, "Imprimir" sends a semantic payload through AndroidBridge.printQr so Android can
// format it for the built-in printer. Desktop/Windows keeps the existing local-agent and browser
// fallbacks. The one-time URL is never logged or persisted by the print bridge.
export default function PrepayQrModal({ show, onClose, tableId, tableName }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [qrData, setQrData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState("");
  const androidQrPrintAvailable = kinposQrPrintBridge.isAvailable();

  const generate = async () => {
    if (!tableId) return;
    setLoading(true);
    setError("");
    setPrintError("");
    setCopied(false);
    try {
      const token = localStorage.getItem("x-access-machine-token");
      const response = await TableSessionsFetchAPI.generatePrepayQr(tableId, token);
      setQrData(response?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo generar el QR. Intenta de nuevo.");
      setQrData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, tableId]);

  useEffect(() => {
    if (!show) return;
    if (androidQrPrintAvailable) {
      setPrinters([]);
      return;
    }
    (async () => {
      try {
        const machine = JSON.parse(localStorage.getItem("machine"));
        const token = localStorage.getItem("x-access-machine-token");
        const response = await PrinterFetchAPI.getPrinter(token, machine.id);
        setPrinters(response?.data?.printers || []);
      } catch (err) {
        setPrinters([]);
      }
    })();
  }, [show, androidQrPrintAvailable]);

  const copyUrl = async () => {
    if (!qrData?.public_url) return;
    try {
      await navigator.clipboard.writeText(qrData.public_url);
    } catch (_) {
      const input = document.createElement("textarea");
      input.value = qrData.public_url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const printTicket = async () => {
    if (!qrData?.public_url || printing) return;
    setPrinting(true);
    setPrintError("");
    try {
      if (androidQrPrintAvailable) {
        kinposQrPrintBridge.printTableQr({
          qrValue: qrData.public_url,
          tableId,
          tableName,
        });
        return;
      }

      if (printers.length === 0) {
        window.print();
        return;
      }

      await printData({
        Texto: `Mesa ${tableName || ""}\nEscanea, pide y paga\nValido para un solo uso`,
        QR: qrData.public_url,
      }, printers);
    } catch (err) {
      setPrintError(err?.message || "No se pudo enviar el QR a la impresora.");
    } finally {
      setPrinting(false);
    }
  };

  const printTicketInBrowser = () => {
    window.print();
  };

  return (

    <>
      {show && (
        <div className="ord-modal-backdrop show d-print-none" onMouseDown={onClose}>
          <div className="ord-modal ord-scope" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" style={{ width: "min(460px, 100%)" }}>
            <div className="ord-modal-header d-print-none">
              <div className="ord-modal-title">QR para pedir y pagar — {tableName || "Mesa"}</div>
              <button className="ord-btn ord-btn-secondary ord-btn-icon" type="button" onClick={onClose} title="Cerrar">
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <div className="ord-modal-body">
              {loading ? (
                <div className="ord-qr-state d-print-none">Generando QR...</div>
              ) : error ? (
                <div className="ord-qr-state d-print-none">
                  <p className="ord-qr-error">{error}</p>
                  <button className="ord-btn ord-btn-primary" type="button" onClick={generate}>
                    Reintentar
                  </button>
                </div>
              ) : qrData?.public_url ? (
                <div className="ord-qr">
                  <p className="ord-qr-hint d-print-none">
                    El cliente escanea este QR, pide y paga sin esperar autorizacion. Una vez que lo
                    escanea queda invalido - genera uno nuevo para el siguiente cliente.
                  </p>

                  {/* Only this block survives onto the printed page - see the #prepay-qr-print-area rules
                in reservation.css. */}
                  <div id="prepay-qr-print-area" className="rfm-prepay-print-ticket">
                    <p className="rfm-prepay-print-title">{tableName ? `Mesa ${tableName}` : "Mesa"}</p>
                    <p className="rfm-prepay-print-subtitle">Escanea, pide y paga</p>
                    <div className="ord-qr-frame">
                      <QRCode value={qrData.public_url} size={220} bgColor="#ffffff" fgColor="#000000" level="M" />
                    </div>
                    <p className="rfm-prepay-print-note">Valido para un solo uso</p>
                  </div>

                  <div className="ord-qr-url d-print-none">
                    <input className="ord-qr-input" value={qrData.public_url} readOnly aria-label="URL del QR de pre-pago" />
                    <button className="ord-btn ord-btn-secondary" type="button" onClick={copyUrl}>
                      {copied ? "Copiada" : "Copiar"}
                    </button>
                  </div>
                  {printError ? <p className="ord-qr-error d-print-none">{printError}</p> : null}
                </div>
              ) : (
                <div className="ord-qr-state d-print-none">No fue posible generar el QR.</div>
              )}
            </div>

            <div className="ord-modal-footer d-print-none">
              <div className="ord-footer">
                <button className="ord-btn ord-btn-secondary" type="button" onClick={generate} disabled={loading}>
                  <i className="bi bi-arrow-clockwise" /> Regenerar
                </button>
                <button className="ord-btn ord-btn-primary" type="button" onClick={printTicket} disabled={!qrData?.public_url || printing}>
                  <i className="bi bi-printer" /> {printing ? "Imprimiendo..." : "Imprimir"}
                </button>
                {/* {androidQrPrintAvailable || printers.length > 0 ? (
                  <button className="ord-btn ord-btn-link" type="button" onClick={printTicketInBrowser} disabled={!qrData?.public_url} title="Usar el dialogo de impresion del navegador en vez de la impresora configurada">
                    Imprimir con el navegador
                  </button>
                ) : null} */}
                <span className="ord-spacer" />
                <button className="ord-btn ord-btn-secondary" type="button" onClick={onClose}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </>

  );
}
